import {FC, JSX, Ref, forwardRef, useMemo} from 'react';
import {ThemeProvider, useTheme} from 'styled-components';

import {deepMerge} from '@shared/lib/utils/object_utils';

import {FrontendTheme} from '@shared_web/theme/theme_model';

export function withThemeOverride<BaseProps, Element = HTMLElement>(
  Component: FC<BaseProps>
): FC<BaseProps & {themeOverrides?: FrontendTheme}> {
  const WrappedComponent = forwardRef(
    (
      props: BaseProps & {
        themeOverrides?: FrontendTheme;
      },
      ref: Ref<Element>
    ): JSX.Element => {
      const {themeOverrides, ...rest} = props;
      const theme = useTheme();
      const mergedTheme = useMemo(() => mergeTheme(theme, themeOverrides), [theme, themeOverrides]);
      return (
        <ThemeProvider theme={mergedTheme}>
          <Component ref={ref} {...(rest as typeof props)} />
        </ThemeProvider>
      );
    }
  ) as ReturnType<typeof withThemeOverride<BaseProps, Element>>;

  return WrappedComponent;
}

export function mergeTheme(...args: (FrontendTheme | undefined)[]): FrontendTheme {
  let finalTheme: FrontendTheme = {};
  for (const theme of args) {
    finalTheme = deepMerge(finalTheme, theme ?? {});
  }
  return finalTheme;
}
